import { createReducer } from "typesafe-actions";

import Route, { RoutePreview } from "@mapmycustomers/shared/types/entity/Route";

import {
  Actions,
  clearRouteDetails,
  deleteRoute,
  initializeRouteView,
  rebuildRoutePreview,
  resetAllStops,
  setRoute,
  setRouteStopChecked,
  shareRoute,
  updateRoute,
} from "../actions";

export interface RouteViewState {
  initializing: boolean;
  isPreviewStale: boolean;
  route?: Route;
  routePreview?: RoutePreview;
  routeUpdating: boolean;
  sharing: boolean;
}

export const initialViewState: RouteViewState = {
  initializing: false,
  isPreviewStale: false,
  routeUpdating: false,
  sharing: false,
};

export const viewReducer = createReducer<RouteViewState, Actions>(initialViewState)
  .handleAction(initializeRouteView.request, (state) => ({
    ...state,
    initializing: true,
  }))
  .handleAction(initializeRouteView.success, (state, { payload }) => ({
    ...state,
    initializing: false,
    isPreviewStale: false,
    route: payload.route,
    routePreview: payload.routePreview,
  }))
  .handleAction(initializeRouteView.failure, (state) => ({
    ...state,
    initializing: false,
  }))
  // Deliberately does not touch isPreviewStale. Its two callers disagree: the transfer-stops
  // refresh brings a genuinely fresh preview, while onShareRoute's failure path re-passes the
  // preview already in the store. Leaving the flag set when it's already stale is harmless,
  // but leaving it set after a transfer-stops refresh (a genuinely fresh preview) also dims
  // the now-correct arrival times to 45% opacity — via StopTimingLine's `.timingStale` class
  // on each stop, and separately via RouteContent's own `.staleGap` class on the connecting
  // StopGap — until the rep presses Re-build again. Clearing it unconditionally would instead
  // hide real staleness on the onShareRoute failure path, so this is the safer of two
  // imperfect options.
  .handleAction(setRoute, (state, { payload }) => ({
    ...state,
    route: payload.route,
    routePreview: payload.routePreview,
  }))
  .handleAction(clearRouteDetails, () => initialViewState)
  .handleAction(updateRoute.request, (state) => ({
    ...state,
    routeUpdating: true,
  }))
  .handleAction(updateRoute.success, (state, { payload }) => ({
    ...state,
    isPreviewStale: payload.routePreview ? false : state.isPreviewStale,
    route: payload.route,
    routePreview: payload.routePreview ?? state.routePreview,
    routeUpdating: false,
  }))
  .handleAction(updateRoute.failure, (state) => ({
    ...state,
    routeUpdating: false,
  }))
  .handleAction(setRouteStopChecked.request, (state) => ({
    ...state,
    routeUpdating: true,
  }))
  .handleAction(setRouteStopChecked.success, (state, { payload }) => ({
    ...state,
    isPreviewStale: true,
    route: payload.route,
    routeUpdating: false,
  }))
  .handleAction(setRouteStopChecked.failure, (state) => ({
    ...state,
    routeUpdating: false,
  }))
  .handleAction(resetAllStops.request, (state) => ({
    ...state,
    routeUpdating: true,
  }))
  .handleAction(resetAllStops.success, (state, { payload }) => ({
    ...state,
    isPreviewStale: !payload.routePreview,
    route: payload.route,
    routePreview: payload.routePreview ?? state.routePreview,
    routeUpdating: false,
  }))
  .handleAction(resetAllStops.failure, (state) => ({
    ...state,
    routeUpdating: false,
  }))
  .handleAction(rebuildRoutePreview.request, (state) => ({
    ...state,
    routeUpdating: true,
  }))
  .handleAction(rebuildRoutePreview.success, (state, { payload }) => ({
    ...state,
    isPreviewStale: false,
    route: payload.route,
    routePreview: payload.routePreview,
    routeUpdating: false,
  }))
  .handleAction(rebuildRoutePreview.failure, (state) => ({
    ...state,
    routeUpdating: false,
  }))
  .handleAction(deleteRoute.request, (state) => ({
    ...state,
    routeUpdating: true,
  }))
  .handleAction(deleteRoute.success, (state) => ({
    ...state,
    routeUpdating: false,
  }))
  .handleAction(deleteRoute.failure, (state) => ({
    ...state,
    routeUpdating: false,
  }))
  .handleAction(shareRoute.request, (state) => ({
    ...state,
    sharing: true,
  }))
  .handleAction(shareRoute.success, (state, { payload }) => ({
    ...state,
    route: payload.route,
    sharing: false,
  }))
  .handleAction(shareRoute.failure, (state) => ({
    ...state,
    sharing: false,
  }));

export default viewReducer;
